/**
 * Streaming Text-to-Speech Hook
 *
 * React hook that provides real-time streaming TTS functionality.
 * Processes tokens as they arrive from the LLM, sends them to ElevenLabs
 * via a persistent multi-context WebSocket, and plays audio chunks immediately.
 *
 * Key features:
 * - Process tokens in real-time (synchronous — never drops tokens)
 * - Word-boundary emission with sentence-level flush hints (improved prosody)
 * - Comprehensive streaming-aware text sanitization (markdown, HTML, citations)
 * - Gapless audio playback via Web Audio API
 * - Multi-context WebSocket: interruptions close a context, not the connection
 * - Persistent connection lifecycle (connect once, use across responses)
 * - Eager connection during user gesture (satisfies browser autoplay policy)
 * - A reply ends once, by flush() or finish(), and its turn ends after
 *   ElevenLabs' final message, or when the socket closes while the reply
 *   waits for it (SA-1396)
 * - A reply owns its context: the hook opens it on a connected socket, sends
 *   the reply's words only into it, and closes it once its words have gone
 *   out, even when the reply ended while the socket was connecting (SA-1423)
 */

import { useRef, useCallback, useState, useEffect } from 'react'
import { useSettingsStore } from '@/stores'
import {
  ElevenLabsStreamingService,
  DEFAULT_STREAMING_MODEL_ID,
  type ConnectionState,
  type StreamingOutputFormat,
  type StreamingVoiceSettings,
  type PronunciationDictionaryLocator,
  getSampleRateForFormat,
  isPCMFormat,
} from '@/services/ElevenLabsStreamingService'
import {
  AudioStreamPlayer,
  type AudioPlayerState,
} from '@/services/AudioStreamPlayer'
import { StreamingTextProcessor, type ProcessedText } from '@/utils/streamingSanitization'

export interface UseStreamingTTSOptions {
  /** Voice ID to use for synthesis */
  voiceId: string
  /** Whether streaming TTS is enabled */
  enabled: boolean
  /** ElevenLabs model ID (persona voice_config.model_id); default eleven_flash_v2_5 */
  modelId?: string
  /** ElevenLabs auto_mode (generate on stream stalls); default false — see StreamingTTSConfig.autoMode */
  autoMode?: boolean
  /** Output format for audio */
  outputFormat?: StreamingOutputFormat
  /** Use global endpoint for lower latency */
  useGlobalEndpoint?: boolean
  /** Pre-buffer duration in milliseconds */
  preBufferMs?: number
  /** Voice tuning settings (stability, speed, etc.) */
  voiceSettings?: StreamingVoiceSettings
  /** Pronunciation dictionary locators for ElevenLabs TTS */
  pronunciationDictionaryLocators?: PronunciationDictionaryLocator[]
  /** Callback when TTS starts speaking */
  onStart?: () => void
  /** Callback when TTS stops speaking */
  onEnd?: () => void
  /** Callback on error */
  onError?: (error: Error) => void
}

export interface UseStreamingTTSReturn {
  /** Whether streaming TTS is enabled */
  isEnabled: boolean
  /** WebSocket connection state */
  connectionState: ConnectionState
  /** Whether connected to ElevenLabs */
  isConnected: boolean
  /** Audio playback state */
  playbackState: AudioPlayerState
  /** Whether currently speaking */
  isSpeaking: boolean
  /** Whether buffering audio */
  isBuffering: boolean
  /** Process an incoming token from the LLM stream (synchronous — never drops tokens) */
  processToken: (token: string) => void
  /** End the reply normally: the buffered tail is spoken, then the turn ends. */
  flush: () => void
  /** End the reply without the buffered tail: what was sent plays, then the turn ends. */
  finish: () => void
  /** Immediately stop speaking */
  interrupt: () => void
  /** Connect to ElevenLabs WebSocket. Pass force=true when calling from user gesture where state hasn't updated. */
  connect: (force?: boolean) => Promise<void>
  /** Disconnect from WebSocket */
  disconnect: () => Promise<void>
  /** Reset state for new conversation */
  reset: () => void
  /** Prepare for a new response stream (interrupts current, resets state, initializes AudioContext, starts WebSocket) */
  prepareForNewStream: () => Promise<void>
}

export function useStreamingTTS(
  options: UseStreamingTTSOptions
): UseStreamingTTSReturn {
  const {
    voiceId,
    enabled,
    modelId,
    autoMode,
    outputFormat = 'pcm_22050',
    useGlobalEndpoint = true,
    preBufferMs = 100,
    voiceSettings,
    pronunciationDictionaryLocators,
    onStart,
    onEnd,
    onError,
  } = options

  const { getElevenLabsKey, hasElevenLabsKey } = useSettingsStore()

  // State
  const [connectionState, setConnectionState] =
    useState<ConnectionState>('disconnected')
  const [playbackState, setPlaybackState] = useState<AudioPlayerState>('idle')

  // Refs for mutable objects
  const streamingServiceRef = useRef<ElevenLabsStreamingService | null>(null)
  const audioPlayerRef = useRef<AudioStreamPlayer | null>(null)
  const textProcessorRef = useRef<StreamingTextProcessor | null>(null)
  const hasStartedRef = useRef(false)
  // The current reply has ended its turn (onEnd fired); cleared with hasStartedRef (SA-1402)
  const turnEndedRef = useRef(false)
  const hasErroredRef = useRef(false) // Track if we've had an error to prevent auto-reconnect
  const connectionStateRef = useRef<ConnectionState>('disconnected') // Track state without triggering useEffect
  const pendingChunksRef = useRef<ProcessedText[]>([]) // Queue for items produced before WebSocket is ready
  // How the current reply ended, and whether its context was closed and
  // ElevenLabs' final message has not come yet (SA-1396)
  const replyEndRef = useRef<'open' | 'flushed' | 'finished'>('open')
  const awaitingFinalRef = useRef(false)
  // The reply's own context id, whether a reply was prepared (and not since
  // interrupted, reset or disconnected), and whether it ended while it could
  // not send, so its context closes once its words go out (SA-1423)
  const replyContextRef = useRef<string | null>(null)
  const replyPreparedRef = useRef(false)
  const closePendingRef = useRef(false)
  // How many reconnects the hook itself has under way (a service swap): while
  // above 0, the old service's `disconnected` and a missing service do not
  // strand a pending close (SA-1438)
  const reconnectComingRef = useRef(0)
  // This reply handed ElevenLabs words to speak; without any, its end is
  // certain to bring no audio and the turn ends at once (SA-1438)
  const replySentRef = useRef(false)

  // Refs for callbacks to avoid dependency loops
  const onStartRef = useRef(onStart)
  const onEndRef = useRef(onEnd)
  const onErrorRef = useRef(onError)

  // Keep callback refs up to date
  useEffect(() => {
    onStartRef.current = onStart
    onEndRef.current = onEnd
    onErrorRef.current = onError
  }, [onStart, onEnd, onError])

  // Derived state
  const isConnected = connectionState === 'connected'
  const isSpeaking = playbackState === 'playing'
  const isBuffering = playbackState === 'buffering'

  /**
   * Initialize the text processor
   */
  const getTextProcessor = useCallback(() => {
    if (!textProcessorRef.current) {
      textProcessorRef.current = new StreamingTextProcessor()
    }
    return textProcessorRef.current
  }, [])

  /**
   * End the turn once per reply, from whichever the player reports first:
   * idle after playing, or playback complete with nothing played (SA-1402).
   */
  const endTurn = useCallback(() => {
    if (turnEndedRef.current) return
    turnEndedRef.current = true
    hasStartedRef.current = false
    onEndRef.current?.()
  }, [])

  /**
   * Initialize the audio player
   */
  const getAudioPlayer = useCallback(() => {
    if (!audioPlayerRef.current) {
      const sampleRate = getSampleRateForFormat(outputFormat)
      // Only the current player reports: one replaced or released (its late
      // `idle` after `destroy()`) never moves the new reply (SA-1423).
      const player: AudioStreamPlayer = new AudioStreamPlayer(
        {
          sampleRate,
          channels: 1,
          preBufferMs,
        },
        {
          onStateChange: (state) => {
            if (audioPlayerRef.current !== player) return
            setPlaybackState(state)

            if (state === 'playing' && !hasStartedRef.current) {
              hasStartedRef.current = true
              onStartRef.current?.()
            }

            if (state === 'idle' && hasStartedRef.current) {
              endTurn()
            }
          },
          onError: (error) => {
            if (audioPlayerRef.current !== player) return
            console.error('[useStreamingTTS] Audio player error:', error)
            onErrorRef.current?.(error)
          },
          onPlaybackComplete: () => {
            if (audioPlayerRef.current !== player) return
            endTurn()
          },
        }
      )
      audioPlayerRef.current = player
    }
    return audioPlayerRef.current
  }, [outputFormat, preBufferMs, endTurn])

  /**
   * The one rule for sending (SA-1423): the socket is connected and OPEN (a
   * closing socket still reads connected, SA-1438) and the reply's own
   * context is the service's current one. Anything else queues.
   */
  const canSend = useCallback(
    () =>
      connectionStateRef.current === 'connected' &&
      streamingServiceRef.current?.isOpen() === true &&
      replyContextRef.current !== null &&
      streamingServiceRef.current.getCurrentContextId() === replyContextRef.current,
    []
  )

  /**
   * Send one item into the reply's context, noting whether it held words
   * (whitespace or a bare flush generates nothing, SA-1438).
   */
  const sendItem = useCallback((service: ElevenLabsStreamingService, text: string, flush: boolean) => {
    if (text.trim()) replySentRef.current = true
    service.sendText(text, flush)
  }, [])

  /**
   * Open the reply's own context when a prepared reply still needs one (it is
   * open, or its close is pending) and the connected service's current context
   * is not it: a first connect, a new service, or an earlier reply's context
   * that survived a reconnect (SA-1423).
   */
  const ensureReplyContext = useCallback(() => {
    const service = streamingServiceRef.current
    if (!service || connectionStateRef.current !== 'connected') return
    if (!replyPreparedRef.current) return
    if (replyEndRef.current !== 'open' && !closePendingRef.current) return
    if (
      replyContextRef.current !== null &&
      service.getCurrentContextId() === replyContextRef.current
    ) {
      return
    }
    replyContextRef.current = service.startNewContext()
  }, [])

  /**
   * Send what was queued while the reply could not send, in order, then close
   * the context of a reply that ended meanwhile (SA-1423). Runs right after
   * the reply's context is checked or opened.
   */
  const drainPendingChunks = useCallback(() => {
    const service = streamingServiceRef.current
    if (!service || !canSend()) return

    const pending = pendingChunksRef.current
    pendingChunksRef.current = []
    for (const item of pending) {
      sendItem(service, item.text, item.flush)
    }

    if (closePendingRef.current) {
      closePendingRef.current = false
      service.finalizeContext()
      awaitingFinalRef.current = true
    }
  }, [canSend, sendItem])

  /**
   * End a pending close that nothing will now send: the reply ends with the
   * audio it has (SA-1423, SA-1438).
   */
  const endPendingClose = useCallback(() => {
    if (!closePendingRef.current) return
    closePendingRef.current = false
    audioPlayerRef.current?.markComplete()
  }, [])

  /**
   * Initialize the streaming service.
   * If voiceId, modelId, or pronunciation locators have changed since the
   * service was created, disconnect and recreate to ensure the WebSocket URL
   * and init message have correct settings.
   */
  const getStreamingService = useCallback(() => {
    if (streamingServiceRef.current) {
      // Detect stale voiceId: if the service exists but voiceId changed, recreate it
      const needsRecreate =
        streamingServiceRef.current.getVoiceId() !== voiceId ||
        // modelId and auto_mode ride the WebSocket URL — a change requires a new connection
        streamingServiceRef.current.getModelId() !== (modelId ?? DEFAULT_STREAMING_MODEL_ID) ||
        streamingServiceRef.current.getAutoMode() !== (autoMode ?? false)

      // Detect stale pronunciation locators (connection-level setting, sent in init message)
      const currentLocators = streamingServiceRef.current.getPronunciationDictionaryLocators()
      const locatorsChanged = JSON.stringify(currentLocators) !== JSON.stringify(pronunciationDictionaryLocators)
      if (needsRecreate || locatorsChanged) {
        // The caller connects the new service next (SA-1438)
        reconnectComingRef.current++
        streamingServiceRef.current.disconnect()
        reconnectComingRef.current--
        streamingServiceRef.current = null
        connectionStateRef.current = 'disconnected'
        setConnectionState('disconnected')
      }
    }

    if (!streamingServiceRef.current) {
      const apiKey = getElevenLabsKey()
      if (!apiKey) {
        throw new Error('ElevenLabs API key not configured')
      }

      const service: ElevenLabsStreamingService = new ElevenLabsStreamingService(
        {
          voiceId,
          apiKey,
          modelId,
          autoMode,
          outputFormat,
          useGlobalEndpoint,
          voiceSettings,
          pronunciationDictionaryLocators,
        },
        {
          onAudioChunk: (audioData, format) => {
            if (isPCMFormat(format)) {
              audioPlayerRef.current?.enqueuePCM(audioData)
            } else {
              audioPlayerRef.current?.enqueueMP3(audioData)
            }
          },
          onStateChange: (state) => {
            connectionStateRef.current = state
            setConnectionState(state)
            if (state === 'error') {
              hasErroredRef.current = true
            }
            // A closed socket can no longer send the final message an ended
            // reply waits for: end it with the audio it already has.
            if ((state === 'disconnected' || state === 'error') && awaitingFinalRef.current) {
              awaitingFinalRef.current = false
              audioPlayerRef.current?.markComplete()
            }
            // A reply that ended while it could not send will now never get
            // its words out: end it with what it has (SA-1423). So too on a
            // disconnect no reconnect follows: neither this service's own
            // (the one reporting, not the ref, which a swap moves) nor the
            // hook's (SA-1438)
            if (
              state === 'error' ||
              (state === 'disconnected' &&
                reconnectComingRef.current === 0 &&
                !service.isReconnectScheduled())
            ) {
              endPendingClose()
            }
            // On every connect (the init is already on the socket), give the
            // reply its context, then send what it queued (SA-1423)
            if (state === 'connected') {
              ensureReplyContext()
              drainPendingChunks()
            }
          },
          onError: (error) => {
            console.error('[useStreamingTTS] Streaming service error:', error)
            hasErroredRef.current = true
            onErrorRef.current?.(error)
          },
          onComplete: () => {
            awaitingFinalRef.current = false
            audioPlayerRef.current?.markComplete()
          },
        }
      )
      streamingServiceRef.current = service
    }
    return streamingServiceRef.current
  }, [
    voiceId,
    modelId,
    autoMode,
    outputFormat,
    useGlobalEndpoint,
    voiceSettings,
    pronunciationDictionaryLocators,
    getElevenLabsKey,
    ensureReplyContext,
    drainPendingChunks,
    endPendingClose,
  ])

  /**
   * Connect to ElevenLabs WebSocket
   * @param force - Skip the enabled check (use when calling directly from user gesture where state hasn't updated yet)
   */
  const connectService = useCallback(
    async (force: boolean, onStarted?: () => void) => {
      // onStarted runs once: when the service's connect has started, or when
      // this connect gives up without one. A replaced service's connect never
      // settles, so the swap counts its reconnect done here (SA-1438).
      let started = false
      const markStarted = () => {
        if (started) return
        started = true
        onStarted?.()
      }
      try {
        // When called from toggle handler, state hasn't updated yet, so use force to skip check
        if (!force && !enabled) return
        if (!hasElevenLabsKey) {
          console.warn('[useStreamingTTS] No ElevenLabs API key configured')
          return
        }
        if (!voiceId) {
          console.debug('[useStreamingTTS] No voiceId yet, deferring connection')
          return
        }

        try {
          const audioPlayer = getAudioPlayer()
          await audioPlayer.initialize()

          const service = getStreamingService()
          const connecting = service.connect()
          markStarted()
          await connecting
        } catch (error) {
          console.error('[useStreamingTTS] Connection failed:', error)
          hasErroredRef.current = true
          onErrorRef.current?.(error instanceof Error ? error : new Error('Connection failed'))
        }
      } finally {
        markStarted()
      }
    },
    [enabled, hasElevenLabsKey, voiceId, getAudioPlayer, getStreamingService]
  )

  const connect = useCallback((force = false) => connectService(force), [connectService])

  /**
   * Disconnect from WebSocket entirely.
   * Only call on unmount or when disabling TTS.
   */
  const disconnect = useCallback(async () => {
    if (streamingServiceRef.current) {
      await streamingServiceRef.current.disconnect()
    }
    if (audioPlayerRef.current) {
      audioPlayerRef.current.reset()
    }
    if (textProcessorRef.current) {
      textProcessorRef.current.reset()
    }
    pendingChunksRef.current = []
    hasStartedRef.current = false
    turnEndedRef.current = false
    replyEndRef.current = 'open'
    awaitingFinalRef.current = false
    replyContextRef.current = null
    replyPreparedRef.current = false
    closePendingRef.current = false
    replySentRef.current = false
  }, [])

  /**
   * Process an incoming token from the LLM stream.
   *
   * SYNCHRONOUS: Always buffers the token into StreamingTextProcessor immediately.
   * Emits complete words to ElevenLabs with sentence-boundary flush hints.
   * Sends them into the reply's own context when it can; otherwise queues
   * them for the drain (SA-1423). Never drops tokens, never blocks the event loop.
   */
  const processToken = useCallback(
    (token: string) => {
      // A finished reply takes no more words until the next prepareForNewStream
      if (!enabled || replyEndRef.current === 'finished') return

      // Always feed token into the processor — never skip or drop
      const processor = getTextProcessor()
      const results = processor.processToken(token)

      if (results.length === 0) return

      const service = streamingServiceRef.current
      if (service && canSend()) {
        for (const item of results) {
          sendItem(service, item.text, item.flush)
        }
      } else {
        // Queue items — the drain sends them once the reply's context is open
        pendingChunksRef.current.push(...results)
      }
    },
    [enabled, getTextProcessor, canSend, sendItem]
  )

  /**
   * End the reply normally: the buffered tail is spoken, then the context is
   * closed (flush, then close_context) so ElevenLabs generates what is left
   * and sends its final message. A reply that cannot send yet (the socket is
   * connecting, reconnecting, or a new service is connecting) queues its tail
   * and closes once the drain sends it; one nothing can speak ends at once.
   * A reply ends once: a flush() after flush() or finish() does nothing (SA-1423).
   */
  const flush = useCallback(() => {
    if (replyEndRef.current !== 'open') return

    if (!enabled) {
      audioPlayerRef.current?.markComplete()
      replyEndRef.current = 'flushed'
      return
    }

    const processor = getTextProcessor()
    const remaining = processor.flush()

    const service = streamingServiceRef.current

    // Certainly empty: no words went out, none are queued, no tail. Nothing
    // can come back, so drop the context and end the turn now (SA-1438)
    if (!replySentRef.current && !remaining?.trim() && pendingChunksRef.current.length === 0) {
      replyEndRef.current = 'flushed'
      closePendingRef.current = false
      if (
        service &&
        replyContextRef.current !== null &&
        service.getCurrentContextId() === replyContextRef.current
      ) {
        service.interruptContext()
      }
      audioPlayerRef.current?.markComplete()
      return
    }

    if (remaining) {
      if (service && canSend()) {
        sendItem(service, remaining, true) // flush = true for final text
      } else {
        pendingChunksRef.current.push({ text: remaining, flush: true })
      }
    }

    replyEndRef.current = 'flushed'

    if (service && canSend()) {
      service.finalizeContext()
      awaitingFinalRef.current = true
    } else if (
      (service || reconnectComingRef.current > 0) &&
      replyPreparedRef.current &&
      connectionStateRef.current !== 'error'
    ) {
      // The drain closes the context once the tail has gone out, on this
      // service or the one a swap is bringing (SA-1438)
      closePendingRef.current = true
    } else {
      // Nothing can speak this reply: tell the player no audio is coming
      audioPlayerRef.current?.markComplete()
    }
  }, [enabled, getTextProcessor, canSend, sendItem])

  /**
   * End the reply without the buffered tail (SA-1396): a failed or stopped
   * reply. The words already sent to ElevenLabs are spoken; the words the
   * text processor holds, and anything queued before the socket connected,
   * are dropped. The reply's own context is closed exactly as flush() closes
   * it, so ElevenLabs' final message ends the turn after the last audio. When
   * it cannot send, the player is told at once that no more audio is coming,
   * and the reply's context is dropped so a reconnect does not re-open it
   * (SA-1423).
   */
  const finish = useCallback(() => {
    if (replyEndRef.current !== 'open') return
    replyEndRef.current = 'finished'
    closePendingRef.current = false

    textProcessorRef.current?.reset()
    pendingChunksRef.current = []

    const service = streamingServiceRef.current
    // A reply that sent no words is certain to bring no audio: it ends at
    // once, its context dropped as when it cannot send (SA-1438)
    if (service && canSend() && replySentRef.current) {
      service.finalizeContext()
      awaitingFinalRef.current = true
    } else {
      if (
        service &&
        replyContextRef.current !== null &&
        service.getCurrentContextId() === replyContextRef.current
      ) {
        service.interruptContext()
      }
      audioPlayerRef.current?.markComplete()
    }
  }, [canSend])

  /**
   * Immediately stop speaking.
   * Stops audio, clears buffers, and closes the current context.
   * The WebSocket connection stays alive for the next response.
   */
  const interrupt = useCallback(() => {
    // Stop audio playback
    if (audioPlayerRef.current) {
      audioPlayerRef.current.interrupt()
    }

    // Clear text buffer
    if (textProcessorRef.current) {
      textProcessorRef.current.reset()
    }

    // Clear pending chunks
    pendingChunksRef.current = []

    // Close current context (NOT the WebSocket — it stays alive for next response)
    if (streamingServiceRef.current) {
      streamingServiceRef.current.interruptContext()
    }

    hasStartedRef.current = false
    turnEndedRef.current = false
    replyEndRef.current = 'open'
    awaitingFinalRef.current = false
    replyContextRef.current = null
    replyPreparedRef.current = false
    closePendingRef.current = false
    replySentRef.current = false
    setPlaybackState('stopped')
  }, [])

  /**
   * Reset state for new conversation
   */
  const reset = useCallback(() => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.reset()
    }
    if (textProcessorRef.current) {
      textProcessorRef.current.reset()
    }
    pendingChunksRef.current = []
    hasStartedRef.current = false
    turnEndedRef.current = false
    replyEndRef.current = 'open'
    awaitingFinalRef.current = false
    replyContextRef.current = null
    replyPreparedRef.current = false
    closePendingRef.current = false
    replySentRef.current = false
    setPlaybackState('idle')
  }, [])

  /**
   * Prepare for a new response stream.
   * Interrupts current playback, resets state, and gives the reply its own context.
   *
   * IMPORTANT: This is called from a user gesture (send button click),
   * so we initialize the AudioContext here to satisfy browser autoplay policy.
   *
   * Multi-context: If already connected, opens the reply's context on the
   * existing WebSocket (no reconnection needed). If not connected, eagerly
   * connects; the context opens on the service's 'connected' (SA-1423).
   */
  const prepareForNewStream = useCallback(async () => {
    // SA-1423: the previous reply's messages are dropped from the moment the next reply is prepared
    streamingServiceRef.current?.interruptContext()
    // The previous reply is over: clear its flags before the player reset, so
    // the reset's stopped -> idle ends nothing
    hasStartedRef.current = false
    turnEndedRef.current = false

    // Stop any current audio playback and reset to idle state
    if (audioPlayerRef.current) {
      audioPlayerRef.current.reset()
    }

    // Clear text buffer and pending chunks for new content
    if (textProcessorRef.current) {
      textProcessorRef.current.reset()
    }
    pendingChunksRef.current = []

    // Clear error state to allow reconnection if needed
    hasErroredRef.current = false
    replyEndRef.current = 'open'
    awaitingFinalRef.current = false
    // The new reply owns no context until one opens on a connected socket (SA-1423)
    replyPreparedRef.current = true
    replyContextRef.current = null
    closePendingRef.current = false
    replySentRef.current = false
    setPlaybackState('idle')

    // CRITICAL: Initialize AudioContext during user gesture (send button click)
    // This satisfies browser autoplay policy - AudioContext must be created/resumed
    // in response to user interaction, not from async streaming callbacks
    try {
      const audioPlayer = getAudioPlayer()
      await audioPlayer.initialize()

      // Verify AudioContext is actually running
      if (audioPlayer.getAudioContextState() !== 'running') {
        console.warn('[useStreamingTTS] AudioContext not running after initialize, recreating')
        audioPlayer.destroy()
        audioPlayerRef.current = null
        const newPlayer = getAudioPlayer()
        await newPlayer.initialize()
      }
    } catch (error) {
      console.error('[useStreamingTTS] Failed to initialize AudioContext:', error)
    }

    // Multi-context: If already connected, open the reply's context now.
    // If not connected, eagerly connect; the context opens on 'connected'.
    if (hasElevenLabsKey && voiceId) {
      try {
        const service = getStreamingService()

        if (connectionStateRef.current === 'connected') {
          // Already connected — open the reply's context immediately (no latency!)
          ensureReplyContext()
          drainPendingChunks()
        } else {
          // Not connected — eagerly connect. Fire-and-forget: tokens arriving
          // before the reply's context opens are queued. The context opens on
          // the service's 'connected'; the check after the connect settles is
          // a second look, and reads whatever service is current then (a
          // replaced service's connect never settles) (SA-1423).
          service
            .connect()
            .then(() => {
              ensureReplyContext()
              drainPendingChunks()
            })
            .catch((error) => {
              console.error('[useStreamingTTS] Eager WebSocket connection failed:', error)
              hasErroredRef.current = true
            })
        }
      } catch (error) {
        console.error('[useStreamingTTS] Failed to start WebSocket:', error)
      }
    }
  }, [
    getAudioPlayer,
    getStreamingService,
    hasElevenLabsKey,
    voiceId,
    ensureReplyContext,
    drainPendingChunks,
  ])

  /**
   * Handle enabled state changes.
   * NOTE: We do NOT auto-connect here because AudioContext requires a user gesture.
   * Connection is triggered by:
   * 1. User toggling streaming on (handleTtsToggle calls connect directly)
   * 2. User sending a message (prepareForNewStream starts WebSocket + AudioContext)
   */
  useEffect(() => {
    // Reset error flag when user disables streaming (allows retry on re-enable)
    if (!enabled) {
      hasErroredRef.current = false
    }
  }, [enabled])

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (audioPlayerRef.current) {
        audioPlayerRef.current.destroy()
        audioPlayerRef.current = null
      }
      if (streamingServiceRef.current) {
        streamingServiceRef.current.disconnect()
        streamingServiceRef.current = null
      }
      textProcessorRef.current = null
      pendingChunksRef.current = []
    }
  }, [])

  /**
   * Update service config when options change.
   * Note: pronunciationDictionaryLocators are NOT included here because they are
   * connection-level settings sent only in the WebSocket init message. They are
   * handled by the dedicated effect below.
   */
  useEffect(() => {
    if (streamingServiceRef.current) {
      streamingServiceRef.current.updateConfig({
        voiceId,
        outputFormat,
        useGlobalEndpoint,
        voiceSettings,
      })
    }
  }, [voiceId, outputFormat, useGlobalEndpoint, voiceSettings])

  /**
   * Proactively disconnect when connection-level settings change —
   * pronunciation locators (sent in the WebSocket init message) or modelId
   * (part of the WebSocket URL) — so the next connection uses the updated
   * values.
   *
   * Without this, there's a timing race: the voice session starts before
   * the async dictionary resolution completes, so the first WebSocket
   * connects without locators. When locators arrive later, the existing
   * connection is stale but won't be replaced until getStreamingService()
   * is explicitly called again. This effect forces the stale connection
   * to close immediately, so the next voice interaction reconnects with
   * the correct settings.
   */
  useEffect(() => {
    if (!streamingServiceRef.current) return

    const currentLocators = streamingServiceRef.current.getPronunciationDictionaryLocators()
    const settingsChanged =
      JSON.stringify(currentLocators) !== JSON.stringify(pronunciationDictionaryLocators) ||
      streamingServiceRef.current.getModelId() !== (modelId ?? DEFAULT_STREAMING_MODEL_ID) ||
      streamingServiceRef.current.getAutoMode() !== (autoMode ?? false)

    if (settingsChanged) {
      // Counted before the disconnect, so the old service's `disconnected`
      // does not end a pending close the new service will send (SA-1438)
      const reconnect = Boolean(enabled && hasElevenLabsKey && voiceId)
      if (reconnect) reconnectComingRef.current++

      streamingServiceRef.current.disconnect()
      streamingServiceRef.current = null
      connectionStateRef.current = 'disconnected'
      setConnectionState('disconnected')

      // Auto-reconnect if TTS is currently enabled
      if (reconnect) {
        // Use setTimeout to avoid calling connect during render
        setTimeout(() => {
          void connectService(true, () => {
            reconnectComingRef.current--
            // A pending close no socket will send ends here (SA-1438)
            const service = streamingServiceRef.current
            const state = service?.getState()
            if (
              reconnectComingRef.current === 0 &&
              state !== 'connected' &&
              state !== 'connecting' &&
              !service?.isReconnectScheduled()
            ) {
              endPendingClose()
            }
          })
        }, 0)
      }
    }
  }, [
    pronunciationDictionaryLocators,
    modelId,
    autoMode,
    enabled,
    hasElevenLabsKey,
    voiceId,
    connectService,
    endPendingClose,
  ])

  return {
    isEnabled: enabled,
    connectionState,
    isConnected,
    playbackState,
    isSpeaking,
    isBuffering,
    processToken,
    flush,
    finish,
    interrupt,
    connect,
    disconnect,
    reset,
    prepareForNewStream,
  }
}
